import React, { useEffect, useState } from 'react';
import { Form, Input, Select, DatePicker, TimePicker, InputNumber, Switch, Button, Card, message, Space } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import request from '../../utils/api';

const { TextArea } = Input;

export default function ActivityForm() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activityType, setActivityType] = useState('family');
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  useEffect(() => {
    if (isEdit) loadActivity();
  }, [id]);

  async function loadActivity() {
    try {
      const res = await request.get(`/activities/${id}`);
      const a = res.data;
      form.setFieldsValue({
        ...a,
        activityDate: dayjs(a.activityDate),
        startTime: dayjs(a.startTime, 'HH:mm'),
        endTime: dayjs(a.endTime, 'HH:mm'),
        regDeadline: a.regDeadline ? dayjs(a.regDeadline) : null,
        coverImages: a.coverImages?.join('\n'),
      });
      setActivityType(a.type);
    } catch (e) { message.error('加载活动失败'); }
  }

  async function onFinish(values) {
    setLoading(true);
    try {
      const data = {
        ...values,
        activityDate: values.activityDate.format('YYYY-MM-DD'),
        startTime: values.startTime?.format('HH:mm'),
        endTime: values.endTime?.format('HH:mm'),
        regDeadline: values.regDeadline?.toISOString(),
        coverImages: values.coverImages ? values.coverImages.split('\n').filter(Boolean) : [],
        isChiefOnly: values.isChiefOnly ? 1 : 0,
      };

      if (isEdit) {
        await request.put(`/activities/${id}`, data);
        message.success('更新成功');
      } else {
        data.status = values.status || 'draft';
        await request.post('/activities', data);
        message.success('创建成功');
      }
      navigate('/activities');
    } catch (e) {
      message.error(e.message || '操作失败');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>{isEdit ? '编辑活动' : '创建活动'}</h2>
      <Card>
        <Form form={form} layout="vertical" onFinish={onFinish}
          initialValues={{ type: 'family', status: 'draft', isChiefOnly: false }}
          style={{ maxWidth: 800 }}
        >
          <Form.Item name="title" label="活动名称" rules={[{ required: true, message: '请输入活动名称' }]}>
            <Input placeholder="如：周末亲子露营日" />
          </Form.Item>

          <Form.Item name="type" label="活动类型" rules={[{ required: true }]}>
            <Select onChange={v => setActivityType(v)} options={[
              { value: 'family', label: '亲子家庭' },
              { value: 'social', label: '白领社交' },
              { value: 'corp', label: '企业团建' },
            ]} />
          </Form.Item>

          <Space size={16}>
            <Form.Item name="activityDate" label="活动日期" rules={[{ required: true }]}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="startTime" label="开始时间" rules={[{ required: true }]}>
              <TimePicker format="HH:mm" />
            </Form.Item>
            <Form.Item name="endTime" label="结束时间" rules={[{ required: true }]}>
              <TimePicker format="HH:mm" />
            </Form.Item>
          </Space>

          <Form.Item name="location" label="活动地点" rules={[{ required: true }]}>
            <Input placeholder="如：上海市浦东新区XX路XX号 城市绿地营地" />
          </Form.Item>

          <Form.Item name="maxParticipants" label="人数上限" rules={[{ required: true }]}>
            <InputNumber min={1} max={200} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="regDeadline" label="报名截止时间">
            <DatePicker showTime />
          </Form.Item>

          {/* 价格设置 */}
          {activityType === 'family' && (
            <Space size={16}>
              <Form.Item name="childPrice" label="小孩单价（元）" rules={[{ required: true }]}>
                <InputNumber min={0} precision={2} prefix="¥" />
              </Form.Item>
              <Form.Item name="extraAdultMealPrice" label="额外大人餐费（元）">
                <InputNumber min={0} precision={2} prefix="¥" />
              </Form.Item>
            </Space>
          )}

          {activityType === 'social' && (
            <Space size={16}>
              <Form.Item name="prepayAmount" label="预付金额（元）" rules={[{ required: true }]}>
                <InputNumber min={0} precision={2} prefix="¥" />
              </Form.Item>
              <Form.Item name="totalAmount" label="总金额（元）" rules={[{ required: true }]}>
                <InputNumber min={0} precision={2} prefix="¥" />
              </Form.Item>
            </Space>
          )}

          <Form.Item name="coverImages" label="封面图URL（每行一个）" extra="暂时手动填写图片URL，后续支持上传">
            <TextArea rows={3} placeholder="https://example.com/image1.jpg" />
          </Form.Item>

          <Form.Item name="description" label="活动详情（HTML）">
            <TextArea rows={6} placeholder="<h3>活动介绍</h3><p>...</p>" />
          </Form.Item>

          <Space size={16}>
            <Form.Item name="isChiefOnly" label="大酋长专属" valuePropName="checked">
              <Switch />
            </Form.Item>
            {!isEdit && (
              <Form.Item name="status" label="发布状态">
                <Select options={[
                  { value: 'draft', label: '存为草稿' },
                  { value: 'published', label: '立即发布' },
                ]} />
              </Form.Item>
            )}
          </Space>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEdit ? '保存修改' : '创建活动'}
              </Button>
              <Button onClick={() => navigate('/activities')}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
